import React, { useEffect, useState } from "react";
import { Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live scarcity ticker — "Only X Self Starter slots left".
 *
 * Hits the PUBLIC endpoint `/api/early-adopter/public-stats` every 60s so the
 * number is believable and real. Collapses gracefully when the cap is reached
 * ("All 250 slots claimed. Welcome to CartIQ 360!") and is skipped entirely
 * if the backend is unreachable.
 *
 * Props:
 *   compact  — smaller pill for use inside the hero (true by default)
 *   light    — light-theme variant for the redesigned public Landing page.
 *              Default false = original dark palette preserved everywhere else.
 */
export default function SlotsLeftTicker({ compact = true, light = false }) {
  const [stats, setStats] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/early-adopter/public-stats`);
      if (!res.ok) {
        setHidden(true);
        return;
      }
      setStats(await res.json());
    } catch {
      setHidden(true);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  if (hidden || !stats) return null;

  const { slotsLeft, cap, claimed } = stats;
  const full = slotsLeft <= 0;

  const base =
    "inline-flex items-center gap-2 border font-semibold text-center max-w-full";
  const size = compact
    ? "text-[10px] md:text-[11px] px-2.5 py-1 tracking-[0.16em] uppercase"
    : "text-[10px] sm:text-xs md:text-sm px-3 py-1.5 tracking-[0.14em] sm:tracking-[0.18em] uppercase leading-tight";

  // Light landing palette: green bg + saffron dot & count, dark-green text.
  const tone = light
    ? "border-[#C0DD97] bg-[#EAF3DE] text-[#3B6D11]"
    : full
    ? "border-[#1D9E75] bg-[#1D9E75]/10 text-[#1D9E75]"
    : slotsLeft <= Math.max(20, Math.floor(cap * 0.1))
    ? "border-[#E24B4A] bg-[#E24B4A]/10 text-[#FF8F3F]"
    : "border-[#FF6B00] bg-[#FF6B00]/10 text-[#FF6B00]";

  const countClass = light
    ? "font-mono-num text-[#FF6B00] font-bold"
    : "font-mono-num text-[#F0EDE8]";

  return (
    <div
      className={`${base} ${size} ${tone}`}
      style={light ? { borderWidth: "0.5px" } : undefined}
      data-testid="slots-left-ticker"
      title={`${claimed} of ${cap} Self Starter slots claimed`}
    >
      <Flame
        className={compact ? "w-3 h-3" : "w-3.5 h-3.5"}
        strokeWidth={2.5}
        color="#FF6B00"
      />
      {full ? (
        <span>
          All {cap} Self Starter slots claimed · welcome to CartIQ 360
        </span>
      ) : (
        <span>
          Only <span className={countClass}>{slotsLeft}</span>{" "}
          Self Starter slots left · grab 1 year of Starter free
        </span>
      )}
    </div>
  );
}
